/**
 * Response-Shapes der CalculatorGit-Endpunkte. Die generierten Methoden in
 * {@link apiClient/Api} liefern diese Antworten als `any` (das Backend annotiert
 * die Antworttypen nicht im OpenAPI-Schema), daher werden die DTOs hier
 * handgepflegt — sie spiegeln die C#-Records in `SupportServer/Services/Git/GitContracts.cs`.
 *
 * Werte der String-Enums kommen exakt so vom Server (PascalCase).
 */

/** Zustand einer einzelnen Datei im Arbeitsverzeichnis bzw. im Index. */
export type GitFileState =
    | "Untracked"
    | "Modified"
    | "Added"
    | "Deleted"
    | "Renamed"
    | "Conflicted";

/** Eine geänderte Datei mit Pfad, Zustand und Staging-Information. */
export interface GitFileChange {
    /** Repository-relativer Pfad (Vorwärts-Slashes). */
    readonly path: string;
    /** Der erkannte Zustand der Datei. */
    readonly state: GitFileState;
    /** `true`, wenn die Änderung bereits im Index liegt (staged). */
    readonly staged: boolean;
}

/**
 * Momentaufnahme eines Arbeitsverzeichnisses: aktueller Branch, Verhältnis zum
 * Upstream, ausstehende Änderungen und offene Konflikte.
 */
export interface GitStatus {
    /** Der ausgecheckte Branch (oder `(no branch)` bei detached HEAD). */
    readonly branch: string;
    /** Der getrackte Upstream-Branch oder `null`. */
    readonly upstream: string | null;
    /** Anzahl lokaler Commits, die der Upstream noch nicht hat. */
    readonly aheadBy: number;
    /** Anzahl Upstream-Commits, die lokal noch fehlen. */
    readonly behindBy: number;
    /** Alle erkannten Datei-Änderungen (staged und unstaged). */
    readonly changes: readonly GitFileChange[];
    /** Repository-relative Pfade aller Dateien mit offenem Merge-Konflikt. */
    readonly conflicts: readonly string[];
    /** Gibt an, ob offene Merge-Konflikte vorliegen. */
    readonly hasConflicts: boolean;
    /** Gibt an, ob das Arbeitsverzeichnis sauber ist. */
    readonly isClean: boolean;
}

/** Ergebnis eines `fetch`: was vom Remote geladen wurde. */
export interface GitFetchResult {
    /** Lokale Commits vor dem Upstream nach dem Fetch. */
    readonly aheadBy: number;
    /** Upstream-Commits, die lokal noch fehlen (= integrierbar via pull). */
    readonly behindBy: number;
}

/** Mögliche Ergebnisse einer Merge-/Pull-Operation. */
export type GitMergeOutcome = "UpToDate" | "FastForward" | "Merged" | "Conflicts";

/** Ergebnis eines `pull` (fetch + merge). */
export interface GitPullResult {
    /** Art des Merge-Ergebnisses. */
    readonly outcome: GitMergeOutcome;
    /** SHA des resultierenden Commits (Fast-Forward/Merge) oder `null`. */
    readonly commitSha: string | null;
    /** Bei `Conflicts` die konfliktbehafteten Pfade. */
    readonly conflicts: readonly string[];
}

/** Ergebnis eines `push`. */
export interface GitPushResult {
    /** `true`, wenn alle Refs angenommen wurden. */
    readonly success: boolean;
    /** Bei Ablehnung die Begründung des Remotes, sonst `null`. */
    readonly rejectReason: string | null;
}

/** Ergebnis eines Commits. */
export interface GitCommitResult {
    /** Der vollständige Commit-SHA. */
    readonly sha: string;
    /** Die erste Zeile der Commit-Nachricht. */
    readonly messageShort: string;
}

/** Ein einzelner Commit in der Historie. */
export interface GitCommitInfo {
    /** Vollständiger Commit-SHA. */
    readonly sha: string;
    /** Erste Zeile der Commit-Nachricht. */
    readonly messageShort: string;
    /** Name des Autors. */
    readonly author: string;
    /** Zeitpunkt des Commits (ISO-8601). */
    readonly when: string;
}

/** Diff einer einzelnen Datei (normalisiert). */
export interface GitFileDiff {
    /** Repository-relativer Pfad. */
    readonly path: string;
    /** Änderungsart (Modified/Added/Deleted/…); kann roh vom Server kommen. */
    readonly change: string;
    /** Anzahl hinzugefügter Zeilen. */
    readonly additions: number;
    /** Anzahl entfernter Zeilen. */
    readonly deletions: number;
    /** Unified-Diff-Text. */
    readonly diff: string;
    /** `true`, wenn es sich um eine Binärdatei handelt (kein Text-Diff). */
    readonly isBinary: boolean;
}

/** 3-Wege-Inhalt einer Konfliktdatei (normalisiert). */
export interface GitConflictContent {
    readonly path: string;
    readonly base: string;
    readonly ours: string;
    readonly theirs: string;
}

// Das Backend annotiert die Diff-/Konflikt-Antworten nicht im OpenAPI-Schema (Antwort = any),
// daher werden die Felder hier tolerant aus mehreren möglichen Namen gelesen.
const pickString = (o: any, keys: string[]): string => {
    for (const k of keys) if (typeof o?.[k] === "string") return o[k] as string;
    return "";
};
const pickNumber = (o: any, keys: string[]): number => {
    for (const k of keys) if (typeof o?.[k] === "number") return o[k] as number;
    return 0;
};
const pickBool = (o: any, keys: string[]): boolean => {
    for (const k of keys) if (typeof o?.[k] === "boolean") return o[k] as boolean;
    return false;
};
const scanUnifiedDiff = (o: any): string => {
    for (const v of Object.values(o ?? {})) {
        if (typeof v === "string" && (v.includes("@@") || v.startsWith("diff ") || /\n[+-]/.test(v))) return v;
    }
    return "";
};

/** Wandelt einen rohen Diff-Datensatz tolerant in {@link GitFileDiff} um. */
export const normalizeFileDiff = (raw: any): GitFileDiff => ({
    path: pickString(raw, ["path", "filePath", "file", "Path"]),
    change: pickString(raw, ["change", "changeKind", "state", "status", "kind", "ChangeKind"]),
    additions: pickNumber(raw, ["additions", "added", "insertions", "adds", "Additions"]),
    deletions: pickNumber(raw, ["deletions", "removed", "deleted", "dels", "Deletions"]),
    diff: pickString(raw, ["diff", "unifiedDiff", "patch", "text", "content", "Diff"]) || scanUnifiedDiff(raw),
    isBinary: pickBool(raw, ["isBinary", "binary", "IsBinary"]),
});

/** Normalisiert die Antwort eines Working-/Commit-Diffs zu einer Datei-Liste. */
export const normalizeDiffList = (data: any): GitFileDiff[] => {
    const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.files)
            ? data.files
            : data
                ? [data]
                : [];
    return arr.map(normalizeFileDiff).filter((d: GitFileDiff) => d.path || d.diff);
};

/** Normalisiert die 3-Wege-Konfliktantwort. */
export const normalizeConflict = (data: any): GitConflictContent => ({
    path: pickString(data, ["path", "filePath", "file"]),
    base: pickString(data, ["base", "baseContent", "ancestor", "baseText", "common"]),
    ours: pickString(data, ["ours", "oursContent", "mine", "local", "oursText"]),
    theirs: pickString(data, ["theirs", "theirsContent", "remote", "incoming", "theirsText"]),
});
